import React, { useEffect, useState } from 'react';
import { ArrowRight, Download } from 'lucide-react';
import { impactStats, impactPageContent, images } from '../data/mock';

const Impact = () => {
  const [formData, setFormData] = useState({ fullName: '', email: '' });

  useEffect(() => {
    document.title = 'Impact Reports and Outcomes | ReCircle Foundation';
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        'content',
        "Explore ReCircle Foundation's impact reports, waste recovery outcomes, and decade-long progress building inclusive circular economy systems."
      );
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open the report link
    window.open(impactPageContent.reportLink, '_blank', 'noopener,noreferrer');
  };

  const previousReports = [
    {
      title: "Download Impact Report for FY 2023-24",
      link: "https://heyzine.com/flip-book/e8c15124cb.html"
    },
    {
      title: "Download Impact Report for FY 2022-23",
      link: "https://heyzine.com/flip-book/70a09aca6a.html"
    },
    {
      title: "Download Impact Report for FY 2021-22",
      link: "https://heyzine.com/flip-book/ReCircle"
    }
  ];

  return (
    <div className="pt-20" data-testid="impact-page">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden" data-testid="impact-hero">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.5)), url(${images.impactHero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8 animate-fade-in" data-testid="impact-hero-title">
            {impactPageContent.heroTitle}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-6 max-w-3xl mx-auto leading-relaxed" data-testid="impact-hero-intro">
            {impactPageContent.heroIntro}
          </p>
          <p className="text-lg md:text-xl text-white font-semibold mb-4" data-testid="impact-hero-subtext">
            {impactPageContent.heroSubtext}
          </p>
          <p className="text-lg text-brand-yellow font-medium" data-testid="impact-explore-text">
            {impactPageContent.exploreText}
          </p>
          <p className="text-sm md:text-md text-white  mt-4" data-testid="impact-hero-subtext">
            {impactPageContent.requiredInfoText}
          </p>
        </div>
      </section>

      {/* Impact Stats Section */}
      <section className="py-20 bg-brand-blue text-white" data-testid="impact-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" data-testid="impact-stats-title">
            {impactPageContent.statsTitle}
          </h2>

          {/* Featured Stat - Full Width */}
          <div className="mb-8">
            <div className="text-center p-8 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300" data-testid="featured-stat">
              <div className="text-5xl md:text-6xl font-bold text-brand-yellow mb-4" data-testid="featured-stat-number">
                {impactStats[0].number}
              </div>
              <p className="text-white text-lg md:text-xl leading-relaxed" data-testid="featured-stat-description">
                {impactStats[0].description}
              </p>
            </div>
          </div>

          {/* Other Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.slice(1).map((stat, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white/10 rounded-xl backdrop-blur-sm hover:bg-white/20 transition-all duration-300"
                data-testid={`stat-${index + 1}`}
              >
                <div className="text-3xl md:text-4xl font-bold text-brand-yellow mb-2">
                  {stat.number}
                </div>
                <p className="text-white/90 text-sm leading-relaxed">
                  {stat.description}
                </p>
              </div>
            ))}
          </div>

          {/* Footnote */}
          <p className="text-center text-white/70 text-sm mt-8 italic" data-testid="impact-footnote">
            {impactPageContent.statsFootnote}
          </p>
        </div>
      </section>

      {/* Impact Report Section - Redesigned */}
      <section className="py-20 bg-brand-offwhite" data-testid="impact-report-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 items-start">
            {/* Left Content - 60% */}
            <div className="w-full lg:w-[60%]" data-testid="report-content">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8" data-testid="report-section-title">
                {impactPageContent.reportSectionTitle}
              </h2>
              
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p data-testid="report-description-1">{impactPageContent.reportDescription1}</p>
                <p data-testid="report-description-2">{impactPageContent.reportDescription2}</p>
                <p className="font-semibold text-brand-blue" data-testid="report-cta-text">
                  {impactPageContent.reportCTA}
                </p>
              </div>
            </div>

            {/* Right Form - 40% */}
            <div className="w-full lg:w-[40%]" data-testid="report-form-container">
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Access Our Latest Impact Report
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="report-form">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all duration-200 outline-none"
                      placeholder="Enter your full name"
                      data-testid="input-full-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue transition-all duration-200 outline-none"
                      placeholder="Enter your email address"
                      data-testid="input-email"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full inline-flex items-center justify-center gap-3 bg-cta-blue text-cta-text px-6 py-4 rounded-lg text-lg font-semibold hover:opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02]"
                    data-testid="submit-view-report-btn"
                  >
                    Submit and View Report
                    <ArrowRight size={20} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Previous Impact Reports Section */}
      <section className="py-16 bg-white" data-testid="previous-reports-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10" data-testid="previous-reports-title">
            Read ReCircle’s Impact Reports here
          </h2>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center flex-wrap">
            {previousReports.map((report, index) => (
              <a
                key={index}
                href={report.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-brand-blue text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg hover:scale-[1.02]"
                data-testid={`previous-report-btn-${index}`}
              >
                <Download size={18} />
                {report.title}
              </a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impact;
